import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    getRutasPendientesRevision,
    getGuiasDeRutaPendiente,
    marcarRutaPendienteRevisada,
    ejecutarReconciliacionManual
} from '@apis/mantenedores/nsReconciliacion';

/**
 * Hook para manejar la revisión manual de rutas de NS no encontradas en DispatchTrack
 * (pantalla de visibilidad + registro de revisión, sin reconciliación automática)
 */
export const useNsReconciliacion = () => {
    const user = useSelector(state => state.auth.user);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [rutasPendientes, setRutasPendientes] = useState([]);

    const handleGetRutasPendientesRevision = async (filtros = {}) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await getRutasPendientesRevision(user, filtros);

        if (response.success) {
            setRutasPendientes(response.data);
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    // Detalle de guías (código + subestado vigente) de la ruta/fecha asociada a una fila
    // pendiente de revisión — no usa el loading/error/success compartido de este hook, ya
    // que el llamador (MarcarRevisadoModal) mantiene su propio estado local para no
    // interferir con otras acciones de la página que también usan este hook.
    const handleGetGuiasDeRutaPendiente = async (id) => {
        return await getGuiasDeRutaPendiente(user, id);
    };

    const handleMarcarRutaPendienteRevisada = async (id, observaciones) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await marcarRutaPendienteRevisada(user, id, observaciones);

        if (response.success) {
            setSuccess(response.message);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    // Disparo manual del proceso de reconciliación de NS para una fecha específica (ver
    // docs_myspread/revision_ns/Adicion_Trigger_Manual_Por_Fecha.md, §3, y su rediseño
    // asíncrono en Adicion_Rediseno_Async_Trigger_Manual.md, §5). El backend ahora encola
    // la solicitud y responde casi de inmediato (202, campo `mensaje`, no `message`) - el
    // llamador (index.jsx) maneja su propio estado de carga local para el spinner del
    // botón/modal; este loading/error/success del hook se mantiene solo por consistencia
    // con el resto de las acciones de este hook.
    const handleEjecutarReconciliacionManual = async (fecha) => {
        setLoading(true);
        setError(null);
        setSuccess(null);

        const response = await ejecutarReconciliacionManual(user, fecha);

        if (response.success) {
            setSuccess(response.mensaje);
        } else {
            setError(response.error);
        }

        setLoading(false);
        return response;
    };

    return {
        loading,
        error,
        success,
        rutasPendientes,
        handleGetRutasPendientesRevision,
        handleGetGuiasDeRutaPendiente,
        handleMarcarRutaPendienteRevisada,
        handleEjecutarReconciliacionManual
    };
};
